/**
 * Pure-энкодер `Midi` → байты SMF.
 *
 * Особенности:
 * - VLQ-длины для delta-time и meta-полезных нагрузок;
 * - KeySignature пишется по квинтовому кругу (signed -7..+7);
 * - PitchBend кодируется как 14-битное `value = round(normalized * 8192) + 8192`;
 * - TrackName пишется в начале трека; если есть `instrument.name`,
 *   дополнительно эмитится `instrumentName` meta;
 * - Все события внутри трека собираются в единый массив и stable-сортируются
 *   по тикам, сохраняя порядок вставки в рамках одного тика;
 * - SysEx из `track.sysex` записываются как сырые F0/F7-блоки;
 * - Format-0: все header- и track-события объединяются в один MTrk.
 */

import {
  DRUM_CHANNEL,
  type Midi,
  type MidiChannel,
  type MidiHeader,
  type MidiMetaEvent,
  type MidiPitchBend,
  type MidiSysEx,
  type MidiTrack,
} from "./types.js";
import {
  encodeKeySignatureBytes,
  encodeTempoBytes,
  encodeTimeSignatureBytes,
  encodeUtf8,
  textTypeToByte,
  writeMetaBytes,
} from "./internal/meta-events.js";
import { writeVLQ } from "./internal/vlq.js";

const writeUint16BE = (out: number[], value: number): void => {
  out.push((value >> 8) & 0xff, value & 0xff);
};

const writeUint32BE = (out: number[], value: number): void => {
  out.push(
    (value >>> 24) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 8) & 0xff,
    value & 0xff
  );
};

const clamp7 = (n: number): number => Math.max(0, Math.min(127, Math.round(n)));

type TimedEvent = {
  ticks: number;
  bytes: number[];
};

const compareTicks = (a: TimedEvent, b: TimedEvent): number => a.ticks - b.ticks;

const buildDeltaTrackBytes = (events: TimedEvent[]): number[] => {
  events.sort(compareTicks);
  const out: number[] = [];
  let lastTick = 0;
  for (const ev of events) {
    const delta = Math.max(0, ev.ticks - lastTick);
    lastTick = ev.ticks;
    writeVLQ(out, delta);
    for (const b of ev.bytes) out.push(b);
  }
  // SMF §3: every event, including endOfTrack, is preceded by a delta-time.
  // Some parsers (fluidsynth in particular) read a delta after the last
  // payload and bail with "invalid running status" if they hit `ff 2f` first.
  writeVLQ(out, 0);
  writeMetaBytes(out, 0x2f, []);
  return out;
};

const encodeTrackName = (name: string): TimedEvent => {
  const list: number[] = [0xff, 0x03];
  const payload = encodeUtf8(name);
  writeVLQ(list, payload.length);
  for (const b of payload) list.push(b);
  return { ticks: 0, bytes: list };
};

const encodeInstrumentName = (name: string): TimedEvent | null => {
  if (!name) return null;
  const list: number[] = [0xff, 0x04];
  const payload = encodeUtf8(name);
  writeVLQ(list, payload.length);
  for (const b of payload) list.push(b);
  return { ticks: 0, bytes: list };
};

const encodeProgramChanges = (track: MidiTrack): TimedEvent[] => {
  const events: TimedEvent[] = [];
  for (const pc of track.programChanges) {
    const channel = (pc.channel ?? track.channel) & 0x0f;
    events.push({
      ticks: pc.ticks,
      bytes: [0xc0 | channel, clamp7(pc.value)],
    });
  }
  return events;
};

const encodeNoteEvents = (track: MidiTrack): TimedEvent[] => {
  const events: TimedEvent[] = [];
  for (const n of track.notes) {
    const channel = (n.channel ?? track.channel) & 0x0f;
    const onBytes = [
      0x90 | channel,
      clamp7(n.midi),
      clamp7(Math.round(n.velocity * 127)),
    ];
    events.push({ ticks: n.ticks, bytes: onBytes });
    const offBytes = [
      0x80 | channel,
      clamp7(n.midi),
      clamp7(Math.round((n.noteOffVelocity ?? 64 / 127) * 127)),
    ];
    events.push({ ticks: n.ticks + Math.max(1, n.durationTicks), bytes: offBytes });
  }
  return events;
};

const encodeControlChanges = (track: MidiTrack): TimedEvent[] => {
  const events: TimedEvent[] = [];
  for (const [ccNumber, list] of track.controlChanges) {
    for (const cc of list) {
      const channel = (cc.channel ?? track.channel) & 0x0f;
      events.push({
        ticks: cc.ticks,
        bytes: [0xb0 | channel, clamp7(cc.number), clamp7(cc.value)],
      });
    }
  }
  return events;
};

const encodePitchBends = (track: MidiTrack): TimedEvent[] => {
  const events: TimedEvent[] = [];
  for (const pb of track.pitchBends as ReadonlyArray<MidiPitchBend>) {
    const channel = (pb.channel ?? track.channel) & 0x0f;
    const raw = Math.max(0, Math.min(16383, Math.round(pb.value * 8192) + 8192));
    const lsb = raw & 0x7f;
    const msb = (raw >> 7) & 0x7f;
    events.push({
      ticks: pb.ticks,
      bytes: [0xe0 | channel, lsb, msb],
    });
  }
  return events;
};

const encodeSysex = (track: MidiTrack): TimedEvent[] => {
  const events: TimedEvent[] = [];
  for (const sx of track.sysex as ReadonlyArray<MidiSysEx>) {
    const status = sx.isF0 ? 0xf0 : 0xf7;
    const list: number[] = [status];
    writeVLQ(list, sx.bytes.length);
    for (const b of sx.bytes) list.push(b);
    events.push({ ticks: sx.ticks, bytes: list });
  }
  return events;
};

const encodeTrackMeta = (track: MidiTrack): TimedEvent[] => {
  const events: TimedEvent[] = [];
  for (const m of track.meta as ReadonlyArray<MidiMetaEvent>) {
    const typeByte = textTypeToByte(m.type);
    if (typeByte === null) continue;
    const list: number[] = [0xff, typeByte];
    const payload = encodeUtf8(m.text ?? "");
    writeVLQ(list, payload.length);
    for (const b of payload) list.push(b);
    events.push({ ticks: m.ticks, bytes: list });
  }
  return events;
};

const encodeUnknownMeta = (track: MidiTrack): TimedEvent[] => {
  const events: TimedEvent[] = [];
  for (const um of track.unknownMeta) {
    const list: number[] = [0xff, um.type];
    writeVLQ(list, um.bytes.length);
    for (const b of um.bytes) list.push(b);
    events.push({ ticks: um.ticks, bytes: list });
  }
  return events;
};

const buildHeaderTrack = (header: MidiHeader): TimedEvent[] => {
  const events: TimedEvent[] = [];
  if (header.name) {
    events.push({
      ticks: 0,
      bytes: (() => {
        const list = [0xff, 0x03];
        const payload = encodeUtf8(header.name);
        writeVLQ(list, payload.length);
        for (const b of payload) list.push(b);
        return list;
      })(),
    });
  }
  for (const t of header.tempos) {
    events.push({
      ticks: t.ticks,
      bytes: writeMetaBytesWithLength([0xff, 0x51], encodeTempoBytes(t)),
    });
  }
  for (const t of header.timeSignatures) {
    events.push({
      ticks: t.ticks,
      bytes: writeMetaBytesWithLength([0xff, 0x58], encodeTimeSignatureBytes(t)),
    });
  }
  for (const k of header.keySignatures) {
    events.push({
      ticks: k.ticks,
      bytes: writeMetaBytesWithLength([0xff, 0x59], encodeKeySignatureBytes(k)),
    });
  }
  for (const m of header.meta as ReadonlyArray<MidiMetaEvent>) {
    const typeByte = textTypeToByte(m.type);
    if (typeByte === null) continue;
    const list: number[] = [0xff, typeByte];
    const payload = encodeUtf8(m.text ?? "");
    writeVLQ(list, payload.length);
    for (const b of payload) list.push(b);
    events.push({ ticks: m.ticks, bytes: list });
  }
  return events;
};

const writeMetaBytesWithLength = (prefix: number[], payload: ReadonlyArray<number>): number[] => {
  const out = prefix.slice();
  writeVLQ(out, payload.length);
  for (const b of payload) out.push(b);
  return out;
};

const buildTrackEvents = (track: MidiTrack): TimedEvent[] => {
  const events: TimedEvent[] = [];
  if (track.name) events.push(encodeTrackName(track.name));
  const instName = encodeInstrumentName(track.instrument.name);
  if (instName) events.push(instName);
  events.push(...encodeProgramChanges(track));
  events.push(...encodeNoteEvents(track));
  events.push(...encodeControlChanges(track));
  events.push(...encodePitchBends(track));
  events.push(...encodeSysex(track));
  events.push(...encodeTrackMeta(track));
  events.push(...encodeUnknownMeta(track));
  return events;
};

export const encodeMidi = (midi: Midi): Uint8Array => {
  const out: number[] = [];

  out.push(0x4d, 0x54, 0x68, 0x64);
  writeUint32BE(out, 6);
  writeUint16BE(out, midi.format === 2 ? 2 : midi.format);
  const headerEvents = buildHeaderTrack(midi.header);

  if (midi.format === 0) {
    if (midi.tracks.length !== 1) {
      throw new Error("Format 0 Midi must have exactly one track");
    }
    const track = midi.tracks[0]!;
    const events = [...headerEvents, ...buildTrackEvents(track)];
    // Stable sort по тикам сохраняет порядок вставки в рамках одного тика.
    events.sort((a, b) => a.ticks - b.ticks);
    const body = buildDeltaTrackBytes(events);
    writeUint16BE(out, 1);
    writeUint16BE(out, midi.header.ppq);
    out.push(0x4d, 0x54, 0x72, 0x6b);
    writeUint32BE(out, body.length);
    for (const b of body) out.push(b);
    return new Uint8Array(out);
  }

  writeUint16BE(out, midi.tracks.length + 1);
  writeUint16BE(out, midi.header.ppq);

  const headerBytes = buildDeltaTrackBytes(headerEvents);
  out.push(0x4d, 0x54, 0x72, 0x6b);
  writeUint32BE(out, headerBytes.length);
  for (const b of headerBytes) out.push(b);

  for (const track of midi.tracks) {
    const events = buildTrackEvents(track);
    events.sort((a, b) => a.ticks - b.ticks);
    const body = buildDeltaTrackBytes(events);
    out.push(0x4d, 0x54, 0x72, 0x6b);
    writeUint32BE(out, body.length);
    for (const b of body) out.push(b);
  }

  return new Uint8Array(out);
};
